/**
 * Settings page component
 * Allows users to configure application settings and manage integrations
 */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Save, Bot, Bell, Github, Code } from 'lucide-react';

import { PageLayout, PageContent, PageTitle, PageDescription } from '@/components/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components"
import { Button } from '@/ui/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/components"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/shared/components"
import { Skeleton } from '@/ui/skeleton';
import { toast } from "sonner";

import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthContext';

// Model data (can be moved to a separate file)
const MODEL_PROVIDERS = {
  "OpenAI": {
    "gpt-4": { display_name: "GPT-4 (8k)", tier: "FREE" },
    "gpt-5-2025-08-07": { display_name: "GPT-5", tier: "FREE" },
    "gpt-5.1": { display_name: "GPT-5.1", tier: "FREE" },
    "gpt-5.2": { display_name: "GPT-5.2", tier: "FREE" },
    "gpt-4o": { display_name: "GPT-4o", tier: "DEV" },
    "gpt-4o-mini": { display_name: "GPT-4o Mini", tier: "FREE" },
    "o3": { display_name: "O3", tier: "FREE" },
    "o3-mini": { display_name: "O3 Mini", tier: "FREE" },
  },
  "Anthropic": {
    "claude-opus-4-20250514": { display_name: "Claude Opus 4", tier: "FREE" },
    "claude-sonnet-4-20250514": { display_name: "Claude Sonnet 4", tier: "FREE" },
    "claude-3-7-sonnet-20250219": { display_name: "Claude 3.7 Sonnet", tier: "FREE" },
    "claude-opus-4-1-20250805": { display_name: "Claude Opus 4.1", tier: "FREE" },
    "claude-sonnet-4-5-20250929": { display_name: "Claude Sonnet 4.5", tier: "FREE" },
    "claude-haiku-4-5-20251001": { display_name: "Claude Haiku 4.5", tier: "FREE" },
  },
  "Google": {
    "gemini-2.5-flash": { display_name: "Gemini 2.5 Flash", tier: "FREE" },
    "gemini-2.5-pro": { display_name: "Gemini 2.5 Pro", tier: "FREE" },
  },
};

const settingsSchema = z.object({
  model: z.string().min(1, "Please select a model."),
});

type SettingsFormValues = z.infer<typeof settingsSchema>;

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  // const { toast } = useToast(); // This is no longer needed
  const [loading, setLoading] = useState(true);

  const form = useForm({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      model: "gpt-4o-mini",
    },
  });

  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase.from('user_settings').select('*').eq('user_id', user.id).single();
        if (error) throw error;
        form.reset({
          model: data.llm_model,
        });
      } catch {
         toast.error("Failed to load user settings.");
      } finally {
        setLoading(false);
      }
    };
    fetchUserSettings();
  }, [user, form]);

  const onSubmit = async (values: SettingsFormValues) => {
    try {
      if (!user?.id) throw new Error("User not authenticated");
      const { error } = await supabase
        .from('user_settings')
        .update({
          llm_model: values.model,
        })
        .eq('user_id', user.id);
      if (error) throw error;
      toast.success("Settings saved successfully.");
    } catch {
      toast.error("Failed to save settings.");
    }
  };

  if (loading) {
     return <PageLayout><PageContent><Skeleton className="h-[400px]" /></PageContent></PageLayout>
  }

  return (
    <PageLayout>
      <PageContent>
        <PageTitle>Settings</PageTitle>
        <PageDescription>Configure your Codii application settings and integrations.</PageDescription>

        <Tabs defaultValue="model" className="mt-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="model"><Bot className="w-4 h-4 mr-2" /> Model</TabsTrigger>
            <TabsTrigger disabled value="notifications"><Bell className="w-4 h-4 mr-2" /> Notifications</TabsTrigger>
            <TabsTrigger value="github" disabled><Github className="w-4 h-4 mr-2" /> GitHub</TabsTrigger>
            <TabsTrigger value="api" disabled><Code className="w-4 h-4 mr-2" /> API</TabsTrigger>
          </TabsList>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <TabsContent value="model">
                <Card>
                  <CardHeader><CardTitle>Model Selection</CardTitle><CardDescription>Select the AI model for code reviews.</CardDescription></CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="model"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Review Model</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue placeholder="Select a model" /></SelectTrigger></FormControl>
                            <SelectContent>
                              {Object.entries(MODEL_PROVIDERS).map(([provider, models]) => (
                                <SelectGroup key={provider}>
                                  <h3 className="px-2 py-1.5 text-sm font-semibold">{provider}</h3>
                                  {Object.entries(models).map(([modelId, config]) => (
                                    <SelectItem key={modelId} value={modelId}>
                                      {config.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <Button type="submit" className="mt-6" disabled={form.formState.isSubmitting}>
                <Save className="mr-2 h-4 w-4" /> Save Changes
              </Button>
            </form>
          </Form>
        </Tabs>
      </PageContent>
    </PageLayout>
  );
};

export default SettingsPage;
